import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Upload, List, BarChart3, Settings,
  LogOut, ChevronLeft, ChevronRight, Info, Calendar
} from "lucide-react";

const NAV_ITEMS = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/upload", icon: Upload, label: "Upload" },
  { path: "/transactions", icon: List, label: "Transactions" },
  { path: "/calendar", icon: Calendar, label: "Calendar" },
  { path: "/reports", icon: BarChart3, label: "Reports" },
  { path: "/settings", icon: Settings, label: "Settings" },
  { path: "/about", icon: Info, label: "About" },
];

// ALL colours hardcoded — no Tailwind dependency
const SIDEBAR_BG      = "#1A4D2E";
const LOGO_ICON_BG    = "rgba(255,255,255,0.15)";  // green-tinted, NOT blue
const TEXT_DIM        = "rgba(255,255,255,0.65)";
const TEXT_BRIGHT     = "#ffffff";
const ACTIVE_BG       = "rgba(255,255,255,0.18)";
const HOVER_BG        = "rgba(255,255,255,0.10)";
const BORDER_DIM      = "rgba(255,255,255,0.10)";

export default function Layout() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const user = JSON.parse(localStorage.getItem("taxbuddy_user") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("taxbuddy_token");
    localStorage.removeItem("taxbuddy_user");
    navigate("/login");
  };

  const w = collapsed ? 64 : 220;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F7F8F5", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* SIDEBAR */}
      <aside style={{
        width: w, minWidth: w, background: SIDEBAR_BG,
        display: "flex", flexDirection: "column",
        transition: "width 0.25s ease", overflow: "hidden",
        flexShrink: 0,
      }}>

        {/* Logo */}
        <div style={{
          display: "flex", alignItems: "center",
          gap: collapsed ? 0 : 10,
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "16px 0" : "16px 14px",
          borderBottom: `1px solid ${BORDER_DIM}`,
        }}>
          {/* Icon — always green, never blue */}
          <div style={{
            width: 34, height: 34,
            background: LOGO_ICON_BG,
            borderRadius: 9,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
              <path d="M24,40 C12,32 8,20 16,10 C20,4 28,4 32,10 C40,20 36,32 24,40Z" fill="white" opacity="0.95"/>
              <line x1="24" y1="38" x2="24" y2="12" stroke="rgba(26,77,46,0.5)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>

          {!collapsed && (
            <div style={{ overflow: "hidden" }}>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, lineHeight: 1.2, whiteSpace: "nowrap" }}>TaxBuddy</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 9, fontWeight: 600, letterSpacing: "0.4px", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                AU Tax Record Organiser
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "10px 6px", display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              title={collapsed ? label : undefined}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center",
                gap: collapsed ? 0 : 10,
                justifyContent: collapsed ? "center" : "flex-start",
                padding: collapsed ? "10px 0" : "9px 10px",
                borderRadius: 8,
                textDecoration: "none",
                fontSize: 13, fontWeight: 500,
                color: isActive ? TEXT_BRIGHT : TEXT_DIM,
                background: isActive ? ACTIVE_BG : "transparent",
                transition: "background 0.15s, color 0.15s",
              })}
              onMouseEnter={e => { if (!e.currentTarget.style.background.includes("0.18")) e.currentTarget.style.background = HOVER_BG; }}
              onMouseLeave={e => { if (!e.currentTarget.style.background.includes("0.18")) e.currentTarget.style.background = "transparent"; }}
            >
              <Icon size={16} />
              {!collapsed && label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: "10px 6px", borderTop: `1px solid ${BORDER_DIM}` }}>
          {!collapsed && user?.name && (
            <div style={{ padding: "4px 10px 8px" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Signed in as</div>
              <div style={{ fontSize: 13, color: "#fff", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.name || user.email}
              </div>
            </div>
          )}

          <button onClick={handleLogout}
            style={{
              display: "flex", alignItems: "center", gap: collapsed ? 0 : 8,
              justifyContent: collapsed ? "center" : "flex-start",
              width: "100%", padding: collapsed ? "10px 0" : "8px 10px",
              borderRadius: 8, border: "none", cursor: "pointer",
              background: "transparent", color: TEXT_DIM, fontSize: 13,
              fontFamily: "inherit",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = HOVER_BG; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = TEXT_DIM; }}
          >
            <LogOut size={14} />
            {!collapsed && "Sign Out"}
          </button>

          <button onClick={() => setCollapsed(!collapsed)}
            style={{
              display: "flex", alignItems: "center", gap: collapsed ? 0 : 8,
              justifyContent: collapsed ? "center" : "flex-start",
              width: "100%", padding: collapsed ? "10px 0" : "8px 10px",
              borderRadius: 8, border: "none", cursor: "pointer",
              background: "transparent", color: "rgba(255,255,255,0.35)", fontSize: 13,
              fontFamily: "inherit",
            }}
            onMouseEnter={e => e.currentTarget.style.background = HOVER_BG}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            {collapsed ? <ChevronRight size={14} /> : <><ChevronLeft size={14} /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Outlet />
        <div style={{
          padding: "10px 24px", background: "#fff",
          borderTop: "1px solid #E2EBE0",
          fontSize: 11, color: "#9CA3AF",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ color: "#D97706" }}>⚠</span>
          TaxBuddy is a tax categorisation tool only. It does not provide tax advice. Always consult a registered tax agent before lodging your return.
        </div>
      </main>
    </div>
  );
}
